import type { ReactNode } from 'react'
import type { UIMessage } from 'ai'
import { useTranslation } from 'react-i18next'
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from '@/components/ai-elements/tool'
import { LlmChatToolCelexScopeSources } from './LlmChatToolCelexScopeSources'
import { LlmChatToolProductRecommendationsCard } from './LlmChatToolProductRecommendationsCard'
import { toolPartDisplayName } from './llmChatToolPartHelpers'
import { LlmChatToolRegulationSources } from './LlmChatToolRegulationSources'
import { LlmChatToolWebSources } from './LlmChatToolWebSources'
import type { LegislationCitationTarget } from './llmChatToolOutput'

type ToolPart = Extract<
  UIMessage['parts'][number],
  { type: `tool-${string}` } | { type: 'dynamic-tool' }
>

export type LlmChatAssistantToolCallBlockProps = {
  part: ToolPart
  onOpenCitation: (target: LegislationCitationTarget) => void
  sessionId?: string | null
  productId?: string | null
  isChatStreamBusy?: boolean
  onContinueAfterCatalogSave?: (recommendationId: string) => void
  onContinueAfterCatalogDismiss?: (recommendationId: string) => void
}

export function LlmChatAssistantToolCallBlock({
  part,
  onOpenCitation,
  sessionId,
  productId,
  isChatStreamBusy,
  onContinueAfterCatalogSave,
  onContinueAfterCatalogDismiss,
}: LlmChatAssistantToolCallBlockProps) {
  const { t } = useTranslation('llmChat')

  const displayName = toolPartDisplayName(part)
  const isOutputAvailable =
    part.state === 'output-available' && 'output' in part
  const errorText =
    part.state === 'output-error' && 'errorText' in part ? part.errorText : null
  const inputValue = 'input' in part ? part.input : undefined

  const isRagChunksTool =
    part.type === 'dynamic-tool' &&
    part.toolName === 'search_radar_rag_chunks_hybrid' &&
    isOutputAvailable
  const isCelexScopesTool =
    part.type === 'dynamic-tool' &&
    part.toolName === 'search_radar_celex_scopes_hybrid' &&
    isOutputAvailable
  const isTrustedWebSourcesTool =
    part.type === 'dynamic-tool' &&
    part.toolName === 'search_trusted_web_sources' &&
    isOutputAvailable
  const isProductContextTool =
    part.type === 'dynamic-tool' &&
    (part.toolName === 'record_product_context' ||
      part.toolName === 'suggest_product_recommendations' ||
      part.toolName === 'collect_inferred_facts') &&
    isOutputAvailable

  let richOutput: ReactNode = null
  if (isRagChunksTool) {
    richOutput = (
      <LlmChatToolRegulationSources
        output={part.output}
        emptyLabel={t('toolNoSourcesFound')}
        openInViewerLabel={t('openInViewer')}
        viewOnEurLexLabel={t('viewOnEurLex')}
        onOpenCitation={onOpenCitation}
      />
    )
  } else if (isCelexScopesTool) {
    richOutput = (
      <LlmChatToolCelexScopeSources
        output={part.output}
        emptyLabel={t('toolNoSourcesFound')}
        viewOnEurLexLabel={t('viewOnEurLex')}
      />
    )
  } else if (isTrustedWebSourcesTool) {
    richOutput = (
      <LlmChatToolWebSources
        output={part.output}
        emptyLabel={t('toolNoWebSourcesFound')}
        failedLabel={t('toolTrustedWebSearchFailed')}
        searchedSourcesLabel={t('toolTrustedWebSourcesChecked')}
        openLinkLabel={t('openExternalLink')}
      />
    )
  } else if (isProductContextTool) {
    return (
      <div className='not-prose mb-2'>
        <LlmChatToolProductRecommendationsCard
          output={part.output}
          sessionId={sessionId}
          productId={productId}
          isChatStreamBusy={isChatStreamBusy}
          onContinueAfterCatalogSave={onContinueAfterCatalogSave}
          onContinueAfterCatalogDismiss={onContinueAfterCatalogDismiss}
        />
      </div>
    )
  }

  return (
    <Tool className='not-prose mb-2'>
      <ToolHeader title={displayName} type={part.type} state={part.state} />
      <ToolContent>
        {inputValue !== undefined ? (
          <ToolInput input={inputValue} parametersLabel={t('toolParameters')} />
        ) : null}
        {richOutput !== null ? (
          <ToolOutput
            output={richOutput}
            errorText={errorText}
            resultLabel={t('toolResult')}
            errorLabel={t('toolError')}
          />
        ) : (
          <ToolOutput
            output={isOutputAvailable ? part.output : undefined}
            errorText={errorText}
            resultLabel={t('toolResult')}
            errorLabel={t('toolError')}
          />
        )}
      </ToolContent>
    </Tool>
  )
}
